import classNames from 'classnames';
import { memo, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { Spinner } from '@air/primitive-spinner';

import { type AssetTransformStatus } from '~/components/EditMode/constants';
import { useEditMode } from '~/components/EditMode/EditModeProvider';
import { useEditModeOptions } from '~/components/EditMode/hooks/useEditModeOptions';
import { useEditModeStatus } from '~/components/EditMode/hooks/useEditModeStatus';

const VISIBLE_STATES = new Set<AssetTransformStatus>(['in-progress', 'saving']);

export const EditModeInProgressOverlay = memo(() => {
  const { isEditModeEnabled } = useEditMode();
  const { status } = useEditModeStatus();
  const { currentEditModeOption } = useEditModeOptions();

  // Keep a ref to the last non-null content
  const lastContentRef = useRef<ReactNode>(null);

  const currentEditMode = useMemo(() => {
    if (!currentEditModeOption) {
      return null;
    }

    if (status === 'saving') {
      return (
        <>
          <Spinner className="size-10" />
          Saving...
        </>
      );
    }

    return (
      <>
        <Spinner className="size-10" />
        {currentEditModeOption.actionPhrase}...
      </>
    );
  }, [currentEditModeOption, status]);

  // Always update the saved content when we have new content
  useEffect(() => {
    if (currentEditMode !== null) {
      lastContentRef.current = currentEditMode;
    }
  }, [currentEditMode]);

  // Clear saved content after fade-out animation completes
  useEffect(() => {
    if (currentEditMode === null && !VISIBLE_STATES.has(status)) {
      // Only clear after animation duration
      const timeout = setTimeout(() => {
        lastContentRef.current = null;
      }, 500); // Match the transition duration

      return () => clearTimeout(timeout);
    }
  }, [currentEditMode, status]);

  if (!isEditModeEnabled) {
    return null;
  }

  // Show current content if available, otherwise show saved content
  const displayContent = currentEditMode ?? lastContentRef.current;

  return (
    <div
      className={classNames(
        'absolute inset-0 flex items-center justify-center transition-opacity duration-500',
        VISIBLE_STATES.has(status) ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <div className="pointer-events-none flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 rounded-8 bg-black/80 p-4 text-gray-10">{displayContent}</div>
      </div>
    </div>
  );
});

EditModeInProgressOverlay.displayName = 'EditModeInProgressOverlay';
